import { supabase } from '../../utils/supabaseClient';
import type { PostgrestSingleResponse } from '@supabase/supabase-js';
import { optionalBoolean, optionalNumber, optionalString } from '../../utils/typeGuards';
import { vettingService } from '../../services/vetting-service';

export type SearchReadiness = 'SQL' | 'Hybrid1' | 'Hybrid2';

export interface ProgramReportRow {
  programId: string;
  programName: string;
  baciProgramCode: string | null;
  programOrganization: string | null;
  websiteUrl: string | null;
  submissionUrl: string | null;
  pastWinnersUrl: string | null;
  vettingStatus: string | null;
  categoryCount: number;
  categoriesWithOriginal: number;
  categoriesMissingOriginal: number;
  categoriesWithDisplay: number;
  categoriesMissingDisplay: number;
  hasTieredPrizes: boolean | null;
  prizesPerCategory: number | null;
  totalWinOpportunities: number | null;
  calculatedWinOpportunities: number | null;
  multiPrizeIndicator: boolean;
  priorityList: boolean;
  searchMode: SearchReadiness;
  hasRawEmbeddings: boolean;
  hasNormalizedEmbeddings: boolean;
  seedSourceName: string | null;
  seedSourceUrl: string | null;
}

export interface BaseProgramFields {
  programId: string;
  programName: string;
  baciProgramCode: string | null;
  programOrganization: string | null;
  websiteUrl: string | null;
  submissionUrl: string | null;
  pastWinnersUrl: string | null;
  vettingStatus: string | null;
  categoryCount: number;
}

export interface PrizeFields {
  hasTieredPrizes: boolean | null;
  totalWinOpportunities: number | null;
  prizesPerCategory: number | null;
}

export interface GenreReportRow extends BaseProgramFields, PrizeFields {
  matchingCategoryCount: number;
  matchingCategoryNames: string[];
  genreChecked: boolean;
  matchSources: string[];
  programOnlyMatch: boolean;
}

const parseSearchReadiness = (value: unknown): SearchReadiness => {
  if (value === 'Hybrid2') return 'Hybrid2';
  if (value === 'Hybrid1') return 'Hybrid1';
  return 'SQL';
};

const mapRpcRowToProgramReportRow = (row: Record<string, unknown>): ProgramReportRow | null => {
  const programId = optionalString(row.program_id);
  if (!programId) return null;

  const categoryCount = optionalNumber(row.category_count) ?? 0;

  return {
    programId,
    programName: optionalString(row.program_name) ?? 'Untitled program',
    baciProgramCode: optionalString(row.baci_program_code),
    programOrganization: optionalString(row.awarding_organization),
    websiteUrl: optionalString(row.website_url),
    submissionUrl: optionalString(row.submission_url),
    pastWinnersUrl: optionalString(row.past_winners_url),
    vettingStatus: optionalString(row.vetting_status),
    categoryCount,
    categoriesWithOriginal: optionalNumber(row.categories_with_original) ?? 0,
    categoriesMissingOriginal: optionalNumber(row.categories_missing_original) ?? 0,
    categoriesWithDisplay: optionalNumber(row.categories_with_display) ?? 0,
    categoriesMissingDisplay: optionalNumber(row.categories_missing_display) ?? 0,
    hasTieredPrizes: optionalBoolean(row.has_tiered_prizes),
    prizesPerCategory: optionalNumber(row.prizes_per_category),
    totalWinOpportunities: optionalNumber(row.total_win_opportunities),
    calculatedWinOpportunities: optionalNumber(row.calculated_win_opportunities),
    multiPrizeIndicator: Boolean(row.multi_prize_indicator),
    priorityList: Boolean(row.priority_list),
    searchMode: parseSearchReadiness(row.search_mode),
    hasRawEmbeddings: Boolean(row.has_raw_embeddings),
    hasNormalizedEmbeddings: Boolean(row.has_normalized_embeddings),
    seedSourceName: optionalString(row.seed_source_name),
    seedSourceUrl: optionalString(row.seed_source_url)
  } satisfies ProgramReportRow;
};

const fetchProgramReportMetrics = async (
  options: { seedOnly?: boolean; limit?: number } = {}
): Promise<ProgramReportRow[]> => {
  const params: Record<string, unknown> = {};
  const defaultParams: Record<string, unknown> = {
    p_seed_only: false,
    p_limit: 500
  };
  
  if (options.seedOnly !== undefined) {
    params.p_seed_only = options.seedOnly;
  } else {
    params.p_seed_only = defaultParams.p_seed_only;
  }
  
  if (options.limit !== undefined) {
    params.p_limit = options.limit;
  } else {
    params.p_limit = defaultParams.p_limit;
  }
  
  const { data, error }: PostgrestSingleResponse<unknown[]> = await (supabase as any)
    .schema('prod')
    .rpc('get_program_report_metrics', params);

  if (error) {
    throw new Error(error.message);
  }

  const rows = Array.isArray(data) ? data : [];
  return rows
    .map((item) => (item && typeof item === 'object' ? mapRpcRowToProgramReportRow(item as Record<string, unknown>) : null))
    .filter((item): item is ProgramReportRow => item !== null);
};

export const fetchDescendingOrderReport = async (): Promise<ProgramReportRow[]> => {
  return await fetchProgramReportMetrics({ seedOnly: false, limit: 500 });
};

export const fetchSeedSourceReport = async (): Promise<ProgramReportRow[]> => {
  return await fetchProgramReportMetrics({ seedOnly: true, limit: 500 });
};

type GenreRpcRow = {
  program_id: unknown;
  program_name: unknown;
  baci_program_code: unknown;
  awarding_organization: unknown;
  website_url: unknown;
  submission_url: unknown;
  past_winners_url: unknown;
  vetting_status: unknown;
  category_count: unknown;
  matching_category_count: unknown;
  matching_category_names: unknown;
  has_tiered_prizes: unknown;
  total_win_opportunities: unknown;
  prizes_per_category: unknown;
  genre_checked?: unknown;
  match_sources?: unknown;
  match_is_program_only?: unknown;
};

const mapGenreRpcRow = (row: GenreRpcRow): GenreReportRow | null => {
  const programId = optionalString(row.program_id);
  if (!programId) return null;

  const matchingNames = Array.isArray(row.matching_category_names)
    ? (row.matching_category_names as unknown[]).filter((value): value is string => typeof value === 'string')
    : [];

  const matchSources = Array.isArray(row.match_sources)
    ? (row.match_sources as unknown[]).filter((value): value is string => typeof value === 'string')
    : [];

  return {
    programId,
    programName: optionalString(row.program_name) ?? 'Untitled program',
    baciProgramCode: optionalString(row.baci_program_code),
    programOrganization: optionalString(row.awarding_organization),
    websiteUrl: optionalString(row.website_url),
    submissionUrl: optionalString(row.submission_url),
    pastWinnersUrl: optionalString(row.past_winners_url),
    vettingStatus: optionalString(row.vetting_status),
    categoryCount: optionalNumber(row.category_count) ?? 0,
    matchingCategoryCount: optionalNumber(row.matching_category_count) ?? matchingNames.length,
    matchingCategoryNames: matchingNames,
    hasTieredPrizes: optionalBoolean(row.has_tiered_prizes),
    totalWinOpportunities: optionalNumber(row.total_win_opportunities),
    prizesPerCategory: optionalNumber(row.prizes_per_category),
    genreChecked: Boolean(optionalBoolean(row.genre_checked)),
    matchSources,
    programOnlyMatch: Boolean(optionalBoolean(row.match_is_program_only))
  } satisfies GenreReportRow;
};

const sanitizeMatchingCategoryNames = (names: string[], keywords: string[]): string[] => {
  if (names.length === 0) return [];

  const normalizedKeywords = keywords
    .map((keyword) => keyword.trim().toLowerCase())
    .filter((keyword) => keyword.length > 0);

  const uniqueNames: string[] = [];
  const seen = new Set<string>();

  names.forEach((name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const lower = trimmed.toLowerCase();
    if (seen.has(lower)) return;
    seen.add(lower);
    uniqueNames.push(trimmed);
  });

  return uniqueNames.filter((name) => {
    const lowerName = name.toLowerCase();
    return !normalizedKeywords.some((keyword) => {
      if (lowerName !== keyword) return false;
      return uniqueNames.some((other) => {
        if (other === name) return false;
        const otherLower = other.toLowerCase();
        return (
          otherLower.startsWith(`${keyword}:`) ||
          otherLower.startsWith(`${keyword} -`) ||
          otherLower.startsWith(`${keyword} –`) ||
          otherLower.startsWith(`${keyword} —`)
        );
      });
    });
  });
};

const sanitizeGenreReportRow = (row: GenreReportRow, keywords: string[]): GenreReportRow => {
  const sanitizedNames = sanitizeMatchingCategoryNames(row.matchingCategoryNames, keywords);
  return {
    ...row,
    matchingCategoryNames: sanitizedNames,
    matchingCategoryCount: sanitizedNames.length
  } satisfies GenreReportRow;
};

export const fetchProgramsByCategoryKeywords = async (
  keywords: string[],
  options: {
    reportId: string;
    caseInsensitive?: boolean;
    limit?: number;
    includeProgramMetadata?: boolean;
    excludedProgramIds?: string[];
    excludedBaciCodes?: string[];
  }
): Promise<GenreReportRow[]> => {
  const params: Record<string, unknown> = {
    p_terms: keywords,
    p_case_insensitive: options.caseInsensitive ?? true,
    p_limit: options.limit ?? 500,
    p_report_id: options.reportId
  };

  if (options.includeProgramMetadata !== undefined) {
    (params as any).p_include_program_metadata = options.includeProgramMetadata;
  }
  if (options.excludedProgramIds && options.excludedProgramIds.length > 0) {
    (params as any).p_excluded_program_ids = options.excludedProgramIds;
  }
  if (options.excludedBaciCodes && options.excludedBaciCodes.length > 0) {
    (params as any).p_excluded_baci_codes = options.excludedBaciCodes;
  }

  const { data, error }: PostgrestSingleResponse<unknown[]> = await (supabase as any)
    .schema('prod')
    .rpc('get_programs_by_category_keywords', params);

  if (error) {
    throw new Error(error.message);
  }

  const rows = Array.isArray(data) ? (data as GenreRpcRow[]) : [];
  const normalizedKeywords = keywords
    .map((keyword) => keyword.trim().toLowerCase())
    .filter((keyword) => keyword.length > 0);

  return rows
    .map((item) => (item && typeof item === 'object' ? mapGenreRpcRow(item as GenreRpcRow) : null))
    .filter((item): item is GenreReportRow => item !== null)
    .map((row) => sanitizeGenreReportRow(row, normalizedKeywords))
    .sort((a, b) => a.programName.localeCompare(b.programName));
};

export const markGenreProgramChecked = async (params: { programId: string; reportId: string; label: string }) => {
  const { programId, reportId, label } = params;
  const response = await vettingService.recordGenreCheck(programId, reportId, label);
  if (!response.success) {
    throw new Error(response.error ?? 'Failed to record genre check');
  }
};
